export const SCHEDULE_EVENT_TYPES = {
  PRIVATE: 'private',
  BLOCKED_TIME: 'blockedTime',
  FAMILY_GOLF: 'familyGolf',
  PGA_JR_LEAGUE: 'pgaJrLeague',
  GROUP_EVENT: 'groupEvent',
}

export const SCHEDULE_EVENT_COLORS = {
  [SCHEDULE_EVENT_TYPES.PRIVATE]: '#00205B',
  [SCHEDULE_EVENT_TYPES.BLOCKED_TIME]: '#70787C',
  [SCHEDULE_EVENT_TYPES.FAMILY_GOLF]: '#49C5B1',
  [SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE]: '#0077C8',
  [SCHEDULE_EVENT_TYPES.GROUP_EVENT]: '#C5A561',
}

/** Schedule private accent — shared by row bars and booking mix charts */
export const SCHEDULE_PRIVATE_CHART_COLOR =
  SCHEDULE_EVENT_COLORS[SCHEDULE_EVENT_TYPES.PRIVATE]

// Lesson pack sessions render as private events on the calendar; this matches
// the lesson-pack chip color in booking-type badges.
export const SCHEDULE_LESSON_PACK_COLOR = '#D2DEE3'

const FAMILY_GOLF_TAGS = new Set([
  'Family Golf',
  'PGA Family Golf',
  'PGA Family Cup',
])

const PGA_JR_LEAGUE_TAGS = new Set(['PGA Jr. League'])

const BLOCKED_TIME_TYPENAMES = new Set([
  'CoachAvailabilityBlockTime',
  'BlockTime',
  'BlockedTime',
])

const SCHEDULE_EVENT_BADGE_STYLES = {
  [SCHEDULE_EVENT_TYPES.PRIVATE]: {
    color: '#001C57',
    backgroundColor: '#E0EAFF',
  },
  [SCHEDULE_EVENT_TYPES.FAMILY_GOLF]: {
    color: '#124840',
    backgroundColor: '#D9F5F0',
  },
  [SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE]: {
    color: '#004B87',
    backgroundColor: '#D9ECFA',
  },
  [SCHEDULE_EVENT_TYPES.BLOCKED_TIME]: {
    color: '#4F5B62',
    backgroundColor: '#ECEFF1',
  },
  [SCHEDULE_EVENT_TYPES.GROUP_EVENT]: {
    color: '#5C4922',
    backgroundColor: '#F1E9D8',
  },
}

const BADGE_TEXT_COLORS = {
  [SCHEDULE_EVENT_TYPES.FAMILY_GOLF]: '#124840',
  [SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE]: '#004B87',
  [SCHEDULE_EVENT_TYPES.BLOCKED_TIME]: '#4F5B62',
  [SCHEDULE_EVENT_TYPES.GROUP_EVENT]: '#5C4922',
  [SCHEDULE_EVENT_TYPES.PRIVATE]: '#001C57',
}

const hexToRgba = (hex, alpha) => {
  const normalized = hex.replace('#', '')
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const getScheduleEventColor = eventType =>
  SCHEDULE_EVENT_COLORS[eventType] ||
  SCHEDULE_EVENT_COLORS[SCHEDULE_EVENT_TYPES.GROUP_EVENT]

export const getScheduleEventBackgroundColor = (eventType, alpha = 0.12) =>
  hexToRgba(getScheduleEventColor(eventType), alpha)

export const getScheduleEventBadgeStyles = eventType => {
  if (SCHEDULE_EVENT_BADGE_STYLES[eventType]) {
    return SCHEDULE_EVENT_BADGE_STYLES[eventType]
  }

  const color = getScheduleEventColor(eventType)

  return {
    color: BADGE_TEXT_COLORS[eventType] || color,
    backgroundColor: getScheduleEventBackgroundColor(eventType),
  }
}

export const getScheduleEventPastAccentColor = eventType =>
  hexToRgba(getScheduleEventColor(eventType), 0.35)

export const getScheduleEventPastBadgeStyles = eventType => {
  if (eventType === SCHEDULE_EVENT_TYPES.PRIVATE) {
    return {
      color: hexToRgba('#001C57', 0.55),
      backgroundColor: hexToRgba('#E0EAFF', 0.7),
    }
  }

  if (eventType === SCHEDULE_EVENT_TYPES.GROUP_EVENT) {
    return {
      color: hexToRgba('#5C4922', 0.55),
      backgroundColor: hexToRgba('#F1E9D8', 0.7),
    }
  }

  if (eventType === SCHEDULE_EVENT_TYPES.FAMILY_GOLF) {
    return {
      color: hexToRgba('#124840', 0.55),
      backgroundColor: hexToRgba('#D9F5F0', 0.7),
    }
  }

  if (eventType === SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE) {
    return {
      color: hexToRgba('#004B87', 0.55),
      backgroundColor: hexToRgba('#D9ECFA', 0.7),
    }
  }

  const color = getScheduleEventColor(eventType)

  return {
    color: hexToRgba(color, 0.55),
    backgroundColor: hexToRgba(color, 0.08),
  }
}

const hasTag = (tags = [], tagSet) => tags.some(tag => tagSet.has(tag))

const isFamilyGolfTemplate = groupEvent => {
  const templateSlug = groupEvent.template?.slug
  return (
    templateSlug === 'family-golf' || hasTag(groupEvent.tags, FAMILY_GOLF_TAGS)
  )
}

const isPgaJrLeagueEvent = groupEvent =>
  hasTag(groupEvent.tags, PGA_JR_LEAGUE_TAGS) ||
  groupEvent.template?.slug === 'jr-league' ||
  groupEvent.template?.slug === 'pga-jr-league'

export const getScheduleEventType = booking => {
  if (BLOCKED_TIME_TYPENAMES.has(booking.__typename)) {
    return SCHEDULE_EVENT_TYPES.BLOCKED_TIME
  }

  if (booking.__typename === 'Session') {
    return SCHEDULE_EVENT_TYPES.PRIVATE
  }

  if (booking.__typename === 'GroupEvent') {
    if (isPgaJrLeagueEvent(booking)) {
      return SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE
    }

    if (isFamilyGolfTemplate(booking)) {
      return SCHEDULE_EVENT_TYPES.FAMILY_GOLF
    }

    return SCHEDULE_EVENT_TYPES.GROUP_EVENT
  }

  return SCHEDULE_EVENT_TYPES.GROUP_EVENT
}
